// TEMPORARY MOCK ADAPTER for Disease Code Management (PRD 08).
// In-memory only — state lives in this module's `groups`, `codes`, and `codeRefs`
// and resets on hard reload. Mutations resolve with ~300 ms simulated latency.
// Errors mimic the backend envelope (DiseaseCodeApiError).
// Backend switch: VITE_USE_MOCK_API=false flips to the real HTTP adapter. See
// disease-code.adapter.ts.
//
// v9 (DEC-DB-PK-BIGINT-001): ids/groupId are BIGINT UNSIGNED → number on the wire.

import {
  DiseaseCodeApiError,
  type CreateDiseaseCodeInput,
  type CreateDiseaseGroupInput,
  type DiseaseCode,
  type DiseaseCodeApi,
  type DiseaseCodeReferenceItem,
  type DiseaseCodeReferences,
  type DiseaseGroup,
  type UpdateDiseaseCodeInput,
  type UpdateDiseaseGroupInput,
} from '../types/disease-code.types';
import { ICD_CHAPTERS } from '../utils/icd-chapters';

const LATENCY_MS = 300;

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value));
const delay = (ms: number = LATENCY_MS): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

// ── Seed: groups (prototype/admin/pages/disease-codes.html init_disease_codes) ──
// Group records WITHOUT `codeCount`; `listGroups` derives the count from `codes`.
interface SeedGroup {
  id: number;
  name: string;
  rangeStart: string;
  rangeEnd: string;
  description: string | null;
}

let groups: SeedGroup[] = [
  { id: 1, name: '정신건강', rangeStart: 'F00', rangeEnd: 'F99', description: '정신 및 행동장애 분류군' },
  { id: 2, name: '신경계', rangeStart: 'G00', rangeEnd: 'G99', description: '중추 및 말초 신경계 질환군' },
  { id: 3, name: '순환기', rangeStart: 'I00', rangeEnd: 'I99', description: '심장 및 혈관계 질환군' },
  { id: 4, name: '내분비', rangeStart: 'E00', rangeEnd: 'E99', description: '호르몬·대사 관련 질환군' },
  { id: 5, name: '근골격', rangeStart: 'M00', rangeEnd: 'M99', description: '근육·뼈·관절 질환군' },
];

let codes: DiseaseCode[] = [
  // Group 1 — 정신건강
  { id: 101, groupId: 1, code: 'F10', name: '알코올 관련 장애', description: '음주 관련 의존 및 행동장애', status: 'ACTIVE' },
  { id: 102, groupId: 1, code: 'F20', name: '조현병', description: '사고·지각 이상이 동반되는 정신장애', status: 'ACTIVE' },
  { id: 103, groupId: 1, code: 'F31', name: '양극성 장애', description: '조증과 우울 삽화가 반복되는 장애', status: 'ACTIVE' },
  { id: 104, groupId: 1, code: 'F32', name: '우울 에피소드', description: '단일 우울 삽화가 주증상인 상태', status: 'ACTIVE' },
  { id: 105, groupId: 1, code: 'F33', name: '재발성 우울장애', description: '우울 삽화가 반복 재발하는 장애', status: 'ACTIVE' },
  { id: 106, groupId: 1, code: 'F40', name: '공포불안장애', description: '특정 대상·상황에 대한 과도한 공포', status: 'INACTIVE' },
  { id: 107, groupId: 1, code: 'F41', name: '기타 불안장애', description: '범불안 등 비공포성 불안장애', status: 'ACTIVE' },
  { id: 108, groupId: 1, code: 'F42', name: '강박장애', description: '강박사고와 강박행동이 지속되는 장애', status: 'ACTIVE' },
  { id: 109, groupId: 1, code: 'F43', name: '외상후 스트레스장애', description: '외상 사건 이후 지속되는 스트레스 반응', status: 'ACTIVE' },
  { id: 110, groupId: 1, code: 'F50', name: '섭식장애', description: '식이행동 이상과 체형 인식 왜곡', status: 'INACTIVE' },
  { id: 111, groupId: 1, code: 'F90', name: '과잉행동장애', description: '주의력결핍·과잉행동 양상', status: 'ACTIVE' },
  { id: 112, groupId: 1, code: 'F14', name: '코카인 관련 장애', description: '코카인 사용 관련 의존 및 중독장애', status: 'INACTIVE' },
  // Group 2 — 신경계
  { id: 201, groupId: 2, code: 'G20', name: '파킨슨병', description: '떨림·경직·운동완만이 특징인 신경퇴행성 질환', status: 'ACTIVE' },
  { id: 202, groupId: 2, code: 'G30', name: '알츠하이머병', description: '진행성 기억력 저하를 보이는 치매', status: 'ACTIVE' },
  { id: 203, groupId: 2, code: 'G35', name: '다발성 경화증', description: '중추신경계 탈수초성 염증질환', status: 'INACTIVE' },
  { id: 204, groupId: 2, code: 'G40', name: '뇌전증', description: '반복적 발작이 발생하는 만성 질환', status: 'ACTIVE' },
  { id: 205, groupId: 2, code: 'G43', name: '편두통', description: '반복되는 박동성 두통 발작', status: 'ACTIVE' },
  { id: 206, groupId: 2, code: 'G47', name: '수면장애', description: '수면의 질과 주기 이상', status: 'ACTIVE' },
  { id: 207, groupId: 2, code: 'G56', name: '수근관증후군', description: '정중신경 압박으로 인한 손저림', status: 'INACTIVE' },
  { id: 208, groupId: 2, code: 'G62', name: '다발성 신경병증', description: '말초신경 손상으로 감각·운동 저하', status: 'ACTIVE' },
  // Group 3 — 순환기
  { id: 301, groupId: 3, code: 'I10', name: '본태성 고혈압', description: '이차 원인 없이 지속되는 고혈압', status: 'ACTIVE' },
  { id: 302, groupId: 3, code: 'I20', name: '협심증', description: '관상동맥 허혈로 발생하는 흉통', status: 'ACTIVE' },
  { id: 303, groupId: 3, code: 'I21', name: '급성 심근경색', description: '관상동맥 폐색으로 심근 괴사 발생', status: 'ACTIVE' },
  { id: 304, groupId: 3, code: 'I50', name: '심부전', description: '심장 기능 저하로 혈액공급 불충분', status: 'INACTIVE' },
  { id: 305, groupId: 3, code: 'I63', name: '뇌경색', description: '뇌혈관 폐색으로 뇌조직 손상', status: 'ACTIVE' },
  { id: 306, groupId: 3, code: 'I67', name: '기타 뇌혈관질환', description: '뇌혈관성 질환의 기타 분류', status: 'ACTIVE' },
  // Group 4 — 내분비
  { id: 401, groupId: 4, code: 'E10', name: '제1형 당뇨병', description: '인슐린 결핍 중심의 당뇨병', status: 'ACTIVE' },
  { id: 402, groupId: 4, code: 'E11', name: '제2형 당뇨병', description: '인슐린 저항성 중심의 당뇨병', status: 'ACTIVE' },
  { id: 403, groupId: 4, code: 'E03', name: '갑상선기능저하증', description: '갑상선 호르몬 저하 상태', status: 'ACTIVE' },
  { id: 404, groupId: 4, code: 'E05', name: '갑상선기능항진증', description: '갑상선 호르몬 과다 상태', status: 'INACTIVE' },
  { id: 405, groupId: 4, code: 'E78', name: '지질대사장애', description: '이상지질혈증 등 지질대사 이상', status: 'ACTIVE' },
  // Group 5 — 근골격
  { id: 501, groupId: 5, code: 'M05', name: '류마티스관절염', description: '자가면역성 만성 관절염', status: 'ACTIVE' },
  { id: 502, groupId: 5, code: 'M15', name: '다발관절증', description: '퇴행성 관절 변화가 다발성으로 발생', status: 'ACTIVE' },
  { id: 503, groupId: 5, code: 'M51', name: '요추 및 기타 추간판장애', description: '추간판 변성 및 탈출 관련 질환', status: 'INACTIVE' },
  { id: 504, groupId: 5, code: 'M79', name: '기타 연조직장애', description: '근막통증 등 연부조직 질환', status: 'ACTIVE' },
];

// Back-reference map keyed by code STRING (matches prototype/admin/pages/disease-codes.html
// `codeRefs`). Editing a code's `.code` may detach its refs — acceptable for a mock; the real
// backend owns referential integrity.
const codeRefs: Record<string, DiseaseCodeReferences> = {
  F32: {
    proItems: [
      { serial: 'P-0001', name: '지속적인 우울감' },
      { serial: 'P-0002', name: '흥미/즐거움 감소' },
      { serial: 'P-0005', name: '복약 상태' },
      { serial: 'P-0012', name: '사회활동 참여' },
    ],
    deploymentModules: [
      { serial: 'M-0001', name: '매일 모듈' },
      { serial: 'M-0002', name: '매주(월) 모듈' },
    ],
  },
  F41: {
    proItems: [
      { serial: 'P-0003', name: '불안감 정도' },
      { serial: 'P-0005', name: '복약 상태' },
    ],
    deploymentModules: [{ serial: 'M-0002', name: '매주(월) 모듈' }],
  },
  I63: {
    proItems: [
      { serial: 'P-0010', name: '신체활동 악화' },
      { serial: 'P-0014', name: '혈압 관리' },
    ],
    deploymentModules: [{ serial: 'M-0003', name: '매주(화) 모듈' }],
  },
};

let nextGroupId = Math.max(...groups.map((g) => g.id)) + 1;
let nextCodeId = Math.max(...codes.map((c) => c.id)) + 1;

/**
 * Mock-only helper: the group a disease code belongs to (or undefined if no such code). Lets the patient
 * registration mock mirror the BE's secondary-diagnosis domain-dependency check (부진단 must belong to the
 * chosen 주진단 group) so mock and HTTP adapters stay in parity.
 */
export function findMockDiseaseCodeGroupId(codeId: number): number | undefined {
  return codes.find((c) => c.id === codeId)?.groupId;
}

function findGroup(id: number): SeedGroup {
  const g = groups.find((x) => x.id === id);
  if (!g) throw new DiseaseCodeApiError('NOT_FOUND', '해당 질병군을 찾을 수 없습니다.');
  return g;
}

function findCode(id: number): DiseaseCode {
  const c = codes.find((x) => x.id === id);
  if (!c) throw new DiseaseCodeApiError('NOT_FOUND', '해당 질병코드를 찾을 수 없습니다.');
  return c;
}

function withCodeCount(g: SeedGroup): DiseaseGroup {
  return {
    id: g.id,
    name: g.name,
    rangeStart: g.rangeStart,
    rangeEnd: g.rangeEnd,
    description: g.description,
    codeCount: codes.filter((c) => c.groupId === g.id).length,
  };
}

// PRD §3.1 — group name required.
function validateGroupName(name: string | undefined): string {
  const trimmed = (name ?? '').trim();
  if (!trimmed) {
    throw new DiseaseCodeApiError('NAME_REQUIRED', '질병군명을 입력해주세요.', {
      fields: { name: '질병군명을 입력해주세요.' },
    });
  }
  return trimmed;
}

// PRD §3.1 — both range codes required; FE only enforces lexicographic ordering
// (rangeStart <= rangeEnd). ICD-10 format validation is BE-owned.
function validateRangePair(rangeStart: string | undefined, rangeEnd: string | undefined): {
  start: string;
  end: string;
} {
  const start = (rangeStart ?? '').trim().toUpperCase();
  const end = (rangeEnd ?? '').trim().toUpperCase();
  if (!start || !end) {
    throw new DiseaseCodeApiError('RANGE_REQUIRED', '시작 코드와 종료 코드를 입력해주세요.', {
      fields: {
        ...(start ? {} : { rangeStart: '시작 코드를 입력해주세요.' }),
        ...(end ? {} : { rangeEnd: '종료 코드를 입력해주세요.' }),
      },
    });
  }
  if (start > end) {
    throw new DiseaseCodeApiError('RANGE_INVALID', '시작 코드는 종료 코드보다 작거나 같아야 합니다.', {
      fields: {
        rangeStart: '시작 코드는 종료 코드보다 작거나 같아야 합니다.',
      },
    });
  }
  return { start, end };
}

// BA decision B: a group's [start, end] must stay within ONE canonical ICD-10
// chapter band (mirrors the BE invariant). The chapter is not persisted, so
// derive it from the codes — find the band containing `start` and require `end`
// to fall inside that same band. This also rejects ranges that span two chapters
// (e.g. C00~D50 crosses C00~D48 and D50~D89).
function ensureWithinChapter(start: string, end: string): void {
  const band = ICD_CHAPTERS.find((c) => start >= c.rangeStart && start <= c.rangeEnd);
  if (!band) {
    throw new DiseaseCodeApiError('RANGE_OUT_OF_CHAPTER', '선택한 ICD-10 챕터 범위를 벗어났습니다.', {
      fields: { rangeStart: '선택한 ICD-10 챕터 범위를 벗어났습니다.' },
    });
  }
  if (end < band.rangeStart || end > band.rangeEnd) {
    throw new DiseaseCodeApiError('RANGE_OUT_OF_CHAPTER', '선택한 ICD-10 챕터 범위를 벗어났습니다.', {
      fields: { rangeEnd: '선택한 ICD-10 챕터 범위를 벗어났습니다.' },
    });
  }
}

// PRD answer #2: ICD-10 ranges must not overlap between groups. Two ranges
// [aStart..aEnd] and [bStart..bEnd] overlap iff aStart <= bEnd && bStart <= aEnd
// (lexicographic). Adjacent ranges (F00~F50 + F51~F99) are allowed.
// Returns the offending group, if any. `excludeGroupId` skips self on update.
function findOverlappingGroup(
  start: string,
  end: string,
  excludeGroupId: number | null,
): SeedGroup | null {
  for (const g of groups) {
    if (excludeGroupId !== null && g.id === excludeGroupId) continue;
    if (start <= g.rangeEnd && g.rangeStart <= end) return g;
  }
  return null;
}

function ensureNoOverlap(start: string, end: string, excludeGroupId: number | null): void {
  const conflict = findOverlappingGroup(start, end, excludeGroupId);
  if (conflict) {
    throw new DiseaseCodeApiError(
      'RANGE_OVERLAP',
      `이미 등록된 범위입니다. (${conflict.name}: ${conflict.rangeStart}~${conflict.rangeEnd}와 중복)`,
      {
        fields: { chapter: '이미 등록된 범위입니다.' },
        meta: {
          conflictGroupId: conflict.id,
          conflictGroupName: conflict.name,
          conflictRangeStart: conflict.rangeStart,
          conflictRangeEnd: conflict.rangeEnd,
        },
      },
    );
  }
}

// PRD §3.1 — both code and name required.
function validateCodeFields(code: string | undefined, name: string | undefined): {
  code: string;
  name: string;
} {
  const cleanCode = (code ?? '').trim().toUpperCase();
  const cleanName = (name ?? '').trim();
  if (!cleanCode || !cleanName) {
    throw new DiseaseCodeApiError('CODE_REQUIRED', '질병코드와 질병명을 입력해주세요.', {
      fields: {
        ...(cleanCode ? {} : { code: '질병코드를 입력해주세요.' }),
        ...(cleanName ? {} : { name: '질병명을 입력해주세요.' }),
      },
    });
  }
  return { code: cleanCode, name: cleanName };
}

// PRD §8 — duplicate code blocked WITHIN the same group (per answer #5).
function ensureUniqueCodeInGroup(
  groupId: number,
  code: string,
  excludeCodeId: number | null,
): void {
  const taken = codes.some(
    (c) => c.groupId === groupId && c.code === code && c.id !== excludeCodeId,
  );
  if (taken) {
    throw new DiseaseCodeApiError('CODE_DUPLICATE', '이미 등록된 질병코드입니다.', {
      fields: { code: '이미 등록된 질병코드입니다.' },
    });
  }
}

// PRD §8 — deletion blocked when a code is linked to PRO items / modules.
// Returns the counts (always >= 0); throws if any link exists.
function ensureNoLinkedReferences(code: DiseaseCode): void {
  const ref = codeRefs[code.code];
  const proItemCount = ref?.proItems.length ?? 0;
  const moduleCount = ref?.deploymentModules.length ?? 0;
  if (proItemCount > 0 || moduleCount > 0) {
    throw new DiseaseCodeApiError(
      'CODE_HAS_LINKED_REFERENCES',
      '연결된 PRO 항목 또는 배포 모듈이 있어 삭제할 수 없습니다. 연결 해제 후 삭제하세요.',
      { meta: { proItemCount, moduleCount } },
    );
  }
}

const EMPTY_REFS: DiseaseCodeReferences = { proItems: [], deploymentModules: [] };

function readRefs(code: string): DiseaseCodeReferences {
  const ref = codeRefs[code];
  if (!ref) return clone(EMPTY_REFS);
  return clone({
    proItems: ref.proItems.slice() as DiseaseCodeReferenceItem[],
    deploymentModules: ref.deploymentModules.slice() as DiseaseCodeReferenceItem[],
  });
}

export const diseaseCodeMockApi: DiseaseCodeApi = {
  async listGroups(): Promise<DiseaseGroup[]> {
    await delay();
    return clone(groups.map(withCodeCount));
  },

  async createGroup(input: CreateDiseaseGroupInput): Promise<DiseaseGroup> {
    await delay();
    const name = validateGroupName(input.name);
    const { start, end } = validateRangePair(input.rangeStart, input.rangeEnd);
    ensureWithinChapter(start, end);
    ensureNoOverlap(start, end, null);

    const created: SeedGroup = {
      id: nextGroupId++,
      name,
      rangeStart: start,
      rangeEnd: end,
      description: input.description?.trim() ? input.description.trim() : null,
    };
    groups = [...groups, created];
    return clone(withCodeCount(created));
  },

  async updateGroup(id: number, input: UpdateDiseaseGroupInput): Promise<DiseaseGroup> {
    await delay();
    const target = findGroup(id);

    const nextName = input.name !== undefined ? validateGroupName(input.name) : target.name;

    let nextStart = target.rangeStart;
    let nextEnd = target.rangeEnd;
    // Validate the pair whenever either side is touched, so we can detect
    // an inconsistent partial update.
    if (input.rangeStart !== undefined || input.rangeEnd !== undefined) {
      const pair = validateRangePair(
        input.rangeStart ?? target.rangeStart,
        input.rangeEnd ?? target.rangeEnd,
      );
      nextStart = pair.start;
      nextEnd = pair.end;
      ensureWithinChapter(nextStart, nextEnd);
      ensureNoOverlap(nextStart, nextEnd, target.id);
    }

    const nextDescription =
      input.description === undefined
        ? target.description
        : input.description?.trim()
          ? input.description.trim()
          : null;

    target.name = nextName;
    target.rangeStart = nextStart;
    target.rangeEnd = nextEnd;
    target.description = nextDescription;
    return clone(withCodeCount(target));
  },

  async deleteGroup(id: number): Promise<void> {
    await delay();
    findGroup(id);
    // Cascade — matches prototype + user answer #4.
    codes = codes.filter((c) => c.groupId !== id);
    groups = groups.filter((g) => g.id !== id);
  },

  async listCodes(groupId: number): Promise<DiseaseCode[]> {
    await delay();
    findGroup(groupId);
    return clone(codes.filter((c) => c.groupId === groupId));
  },

  async createCode(groupId: number, input: CreateDiseaseCodeInput): Promise<DiseaseCode> {
    await delay();
    findGroup(groupId);
    const { code, name } = validateCodeFields(input.code, input.name);
    ensureUniqueCodeInGroup(groupId, code, null);

    const created: DiseaseCode = {
      id: nextCodeId++,
      groupId,
      code,
      name,
      description: input.description?.trim() ? input.description.trim() : null,
      status: input.status,
    };
    codes = [...codes, created];
    return clone(created);
  },

  async updateCode(id: number, input: UpdateDiseaseCodeInput): Promise<DiseaseCode> {
    await delay();
    const target = findCode(id);

    // Partial update: only the supplied fields are validated.
    let nextCode = target.code;
    let nextName = target.name;
    if (input.code !== undefined || input.name !== undefined) {
      const fields = validateCodeFields(
        input.code ?? target.code,
        input.name ?? target.name,
      );
      nextCode = fields.code;
      nextName = fields.name;
    }
    if (input.code !== undefined && nextCode !== target.code) {
      ensureUniqueCodeInGroup(target.groupId, nextCode, target.id);
    }

    const nextDescription =
      input.description === undefined
        ? target.description
        : input.description?.trim()
          ? input.description.trim()
          : null;

    target.code = nextCode;
    target.name = nextName;
    target.description = nextDescription;
    if (input.status !== undefined) target.status = input.status;
    return clone(target);
  },

  async deleteCode(id: number): Promise<void> {
    await delay();
    const target = findCode(id);
    ensureNoLinkedReferences(target);
    codes = codes.filter((c) => c.id !== id);
  },

  async getCodeReferences(id: number): Promise<DiseaseCodeReferences> {
    await delay(150);
    const target = findCode(id);
    return readRefs(target.code);
  },
};
